// O menu da conta, no canto da barra de cima (IDEA-044).
//
// Onde antes estava o "Sair" sozinho está agora um hamburger com duas coisas:
// **Definições** e **Sair**.
//
// Vale a pena dizer porquê, porque o IDEA-027 tirou daqui um hamburger de
// propósito: esse era de NAVEGAÇÃO, e foi substituído pela barra de baixo, que
// se alcança com o polegar. Este não navega pela app — guarda o que é da conta.
// São duas gavetas diferentes com a mesma pega, e a segunda só apareceu agora
// porque até agora não havia nada lá dentro senão o "Sair".
//
// Fecha-se por todos os caminhos que uma pessoa espera: escolher uma opção,
// tocar fora, carregar em Escape, ou mudar de página por outro sítio qualquer.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { signOut } from "@/api/client";
import { IconeMenu, IconeRoda, IconeSair } from "@/components/ui/icones";

export function MenuConta() {
  const [aberto, setAberto] = useState(false);
  const [aSair, setASair] = useState(false);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const queryClient = useQueryClient();
  const reduzir = useReducedMotion();
  const caixa = useRef<HTMLDivElement>(null);

  // Mudar de página fecha o menu. Inclui o caso em que a página mudou por
  // outra via — um widget, o botão de voltar — e o painel ficaria aberto por
  // cima de um ecrã que já não é aquele de onde foi aberto.
  useEffect(() => {
    setAberto(false);
  }, [pathname]);

  useEffect(() => {
    if (!aberto) return;

    function aoTocarFora(evento: PointerEvent) {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    }
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === "Escape") setAberto(false);
    }

    // `pointerdown` e não `click`: fechar no início do gesto é o que faz o
    // toque fora parecer instantâneo em vez de esperar pelo fim do toque.
    document.addEventListener("pointerdown", aoTocarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("pointerdown", aoTocarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  async function sair() {
    setASair(true);
    await signOut();
    // Limpar tudo o que está em cache — senão quem entrasse a seguir neste
    // aparelho veria por um instante os dados da sessão anterior.
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        aria-label="Conta e definições"
        className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
          aberto ? "bg-ouro/15 text-ouro-600" : "text-tinta-700 hover:bg-creme-200/70 hover:text-ouro-600"
        }`}
      >
        <IconeMenu className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {aberto && (
          <motion.div
            role="menu"
            initial={reduzir ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduzir ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-11 z-50 w-48 overflow-hidden rounded-2xl border border-ouro/25 bg-creme-50 py-1 shadow-[0_18px_40px_-18px_rgba(59,47,42,0.45)]"
          >
            <Link
              to="/definicoes"
              role="menuitem"
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200/70 hover:text-ouro-600"
            >
              <IconeRoda className="h-4 w-4" />
              Definições
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => void sair()}
              disabled={aSair}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200/70 hover:text-ouro-600 disabled:opacity-60"
            >
              <IconeSair className="h-4 w-4" />
              {aSair ? "A sair…" : "Sair"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
